import { useContext, useMemo, useState } from "react"

import { DragSourceDataContext } from "shared/context/drag-and-drop/DragSourceDataContext"
import { usePointerPosition } from "shared/lib/drag-and-drop/usePointerPosition"
import { exists } from "shared/lib/exists"
import { useThrottle } from "shared/lib/performance/useThrottle"
import { useEvent } from "shared/lib/useEvent"
import { isDragSource } from "shared/types/drag-and-drop/DragSource"
import { isDropTarget } from "shared/types/drag-and-drop/DropTarget"

import { DragMoveEvent, useDndMonitor } from "@dnd-kit/core"
import z from "zod"

import { TabBarRootProps } from "../TabBar"
import { TabBarContextType } from "../context/TabBarContext"
import { useTabBarViewModel } from "./useTabBarViewModel"

const TabDragMetaSchema = z.object({
  eventKey: z.string(),
  index: z.number()
})

export function useTabBarDraggableViewModel({
  enableSort: _,
  onSort,
  ...rest
}: Omit<TabBarRootProps, "children">) {
  const { context: baseContext, containerProps } = useTabBarViewModel(rest)
  const { dragSource } = useContext(DragSourceDataContext)
  const { getPointerPosition } = usePointerPosition()
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  const handleDragMove = useEvent((event: DragMoveEvent) => {
    if (
      !isDropTarget(event.over?.data.current, {
        metaSchema: TabDragMetaSchema
      }) ||
      event.over.data.current.origin !== dragSource?.origin
    ) {
      return
    }

    const pointerPosition = getPointerPosition()

    if (!pointerPosition) {
      return
    }

    const index = event.over?.data.current.meta.index
    const insertBefore =
      pointerPosition.x < event.over.rect.left + event.over.rect.width / 2

    setHoverIndex?.(insertBefore ? index : index + 1)
  })

  const throttledDragMove = useThrottle(handleDragMove)

  useDndMonitor({
    onDragMove: throttledDragMove,
    onDragEnd: event => {
      setHoverIndex(null)
      if (
        isDragSource(event.active.data.current, {
          metaSchema: TabDragMetaSchema
        }) &&
        exists(hoverIndex)
      ) {
        onSort?.(
          event.active.data.current.meta.eventKey,
          event.active.data.current.meta.index,
          hoverIndex > event.active.data.current.meta.index
            ? hoverIndex - 1
            : hoverIndex
        )
      }
    }
  })

  const context: TabBarContextType = useMemo(
    () => ({ ...baseContext, enableSort: true, hoverIndex }),
    [baseContext, hoverIndex]
  )

  return { context, containerProps }
}
